import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { X, Plus, Maximize2, Settings, Info, ChevronDown } from 'lucide-react';
import { Dropdown } from '../ui/Dropdown';
interface OrderFormProps {
  availableBalance?: number;
  currentPrice?: number;
  currency?: string;
  onSubmitOrder?: (order: any) => void;
}
export function OrderForm({
  availableBalance = 79.19,
  currentPrice = 96470,
  currency = 'USD',
  onSubmitOrder
}: OrderFormProps) {
  const [orderSide, setOrderSide] = useState<'buy' | 'sell'>('buy');
  const [orderType, setOrderType] = useState('Market');
  const [quantity, setQuantity] = useState('0.00005');
  const [limitPrice, setLimitPrice] = useState('');
  const [totalInput, setTotalInput] = useState('');
  const [useMargin, setUseMargin] = useState(true);
  const [sliderValue, setSliderValue] = useState(6);
  const [reduceOnly, setReduceOnly] = useState(false);
  const [tpSlValue, setTpSlValue] = useState('No');
  const [feeCurrency, setFeeCurrency] = useState('USD');
  // When switching to Limit order type, populate with current market price
  const handleOrderTypeChange = useCallback((newOrderType: string) => {
    setOrderType(newOrderType);
    if (newOrderType === 'Limit' && !limitPrice && currentPrice) {
      setLimitPrice(currentPrice.toFixed(1));
    }
  }, [limitPrice, currentPrice]);
  // Memoize static options to prevent recreation
  const orderTypeOptions = useMemo(() => [{
    value: 'Market',
    label: 'Market'
  }, {
    value: 'Limit',
    label: 'Limit'
  }, {
    value: 'Stop loss',
    label: 'Stop loss'
  }, {
    value: 'Stop loss limit',
    label: 'Stop loss limit'
  }, {
    value: 'Take profit',
    label: 'Take profit'
  }, {
    value: 'Take profit limit',
    label: 'Take profit limit'
  }, {
    value: 'Iceberg',
    label: 'Iceberg'
  }, {
    value: 'Trailing stop',
    label: 'Trailing stop'
  }, {
    value: 'Trailing stop limit',
    label: 'Trailing stop limit'
  }], []);
  const tpSlOptions = useMemo(() => [{
    value: 'No',
    label: 'No'
  }, {
    value: 'Simple',
    label: 'Simple'
  }, {
    value: 'Advanced',
    label: 'Advanced'
  }], []);
  const feeCurrencyOptions = useMemo(() => [{
    value: 'USD',
    label: 'USD'
  }, {
    value: 'BTC',
    label: 'BTC'
  }], []);
  const leverageMultiplier = useMargin ? 10 : 1;
  const effectiveBalance = useMargin ? availableBalance * leverageMultiplier : availableBalance;
  const hasPosition = sliderValue > 0;
  const marginHealth = useMargin ? hasPosition ? 100 : 100 : 100;
  const getMarginHealthColor = useCallback(() => {
    if (!useMargin) return 'bg-text-tertiary';
    if (marginHealth >= 100) return 'bg-success';
    if (marginHealth > 50) return 'bg-warning';
    return 'bg-danger';
  }, [useMargin, marginHealth]);
  // Calculate quantity based on slider value
  useEffect(() => {
    if (currentPrice > 0) {
      const usdAmount = effectiveBalance * sliderValue / 100;
      const effectivePrice = orderType === 'Limit' && limitPrice ? parseFloat(limitPrice) : currentPrice;
      const btcQuantity = usdAmount / effectivePrice;
      setQuantity(btcQuantity.toFixed(8));
    }
  }, [sliderValue, effectiveBalance, currentPrice, orderType, limitPrice]);
  const effectivePrice = orderType === 'Limit' && limitPrice ? parseFloat(limitPrice) : currentPrice;
  const total = effectivePrice * parseFloat(quantity);
  // Calculate trading fee (0.2% of total)
  const tradingFeeUSD = total * 0.002;
  const tradingFeeBTC = tradingFeeUSD / currentPrice;
  const displayedFee = feeCurrency === 'USD' ? tradingFeeUSD : tradingFeeBTC;
  // Update totalInput when quantity changes (but not when user is typing in total)
  useEffect(() => {
    if (document.activeElement?.getAttribute('name') !== 'total') {
      setTotalInput(total.toFixed(2));
    }
  }, [total]);
  const handleSubmit = useCallback(() => {
    onSubmitOrder?.({
      side: orderSide,
      type: orderType,
      price: currentPrice,
      quantity: parseFloat(quantity),
      total,
      useMargin
    });
  }, [onSubmitOrder, orderSide, orderType, currentPrice, quantity, total, useMargin]);
  const handleQuantityChange = useCallback((value: string) => {
    setQuantity(value);
    // Update slider to match manual quantity input
    const effectivePrice = orderType === 'Limit' && limitPrice ? parseFloat(limitPrice) : currentPrice;
    if (effectivePrice > 0 && effectiveBalance > 0) {
      const btcQty = parseFloat(value) || 0;
      const usdValue = btcQty * effectivePrice;
      const percentage = Math.min(100, usdValue / effectiveBalance * 100);
      setSliderValue(Math.round(percentage));
    }
  }, [orderType, limitPrice, currentPrice, effectiveBalance]);
  const handleTotalChange = useCallback((value: string) => {
    setTotalInput(value);
    const totalValue = parseFloat(value) || 0;
    const effectivePrice = orderType === 'Limit' && limitPrice ? parseFloat(limitPrice) : currentPrice;
    if (effectivePrice > 0) {
      const btcQty = totalValue / effectivePrice;
      setQuantity(btcQty.toFixed(8));
      // Update slider
      if (effectiveBalance > 0) {
        const percentage = Math.min(100, totalValue / effectiveBalance * 100);
        setSliderValue(Math.round(percentage));
      }
    }
  }, [orderType, limitPrice, currentPrice, effectiveBalance]);
  // Calculate portfolio leverage based on position size
  const currentLeverage = useMemo(() => {
    if (!useMargin) return 0;
    // Assume we have an existing position worth ~$79 (10x leveraged from ~$7.90 margin)
    // This gives us a starting leverage of ~10x
    const existingPositionValue = 79.04; // From the screenshot
    const existingLeverage = existingPositionValue / availableBalance;
    return existingLeverage;
  }, [useMargin, availableBalance]);
  const newLeverage = useMemo(() => {
    if (!useMargin || sliderValue === 0) return currentLeverage;
    // Calculate the change in position
    const existingPositionValue = 79.04;
    const orderValue = total;
    let newPositionValue: number;
    if (reduceOnly) {
      // Selling reduces the position
      newPositionValue = existingPositionValue - orderValue;
    } else {
      // Buying increases the position
      newPositionValue = existingPositionValue + orderValue;
    }
    // New leverage = new position value / account balance
    const leverage = Math.max(0, newPositionValue / availableBalance);
    return leverage;
  }, [useMargin, total, availableBalance, sliderValue, reduceOnly, currentLeverage]);
  // Calculate margin health after position
  const currentMarginHealth = useMemo(() => {
    if (!useMargin) return null;
    // Current position details from positions table:
    // - Position value: ~$79.04 (0.00082670 BTC × $95,790)
    // - Initial margin used: $7.91
    // - Account balance: $79.19
    // CORRECT FORMULA for Margin Health with existing position:
    // Margin Health = (Account Equity / Maintenance Margin Requirement) × 100
    //
    // Where:
    // - Account Equity = Account Balance + Unrealized P&L
    // - Maintenance Margin = Position Value × Maintenance Margin Rate (typically 5% for 10x leverage)
    const existingPositionValue = 79.04;
    const maintenanceMarginRate = 0.05; // 5% for 10x leverage (1/20)
    const maintenanceMarginRequired = existingPositionValue * maintenanceMarginRate;
    // Assuming no unrealized P&L for simplicity (position opened at current price)
    const accountEquity = availableBalance;
    // Margin Health percentage
    const health = accountEquity / maintenanceMarginRequired * 100;
    return health;
  }, [useMargin, availableBalance]);
  const newMarginHealth = useMemo(() => {
    if (!useMargin || sliderValue === 0) return currentMarginHealth;
    const existingPositionValue = 79.04;
    const orderValue = total;
    const maintenanceMarginRate = 0.05; // 5% for 10x leverage
    let newPositionValue: number;
    if (reduceOnly) {
      // Selling reduces position
      newPositionValue = Math.max(0, existingPositionValue - orderValue);
    } else {
      // Buying increases position
      newPositionValue = existingPositionValue + orderValue;
    }
    // Calculate new maintenance margin requirement
    const newMaintenanceMarginRequired = newPositionValue * maintenanceMarginRate;
    // Account equity stays the same (we're not adding/removing funds, just trading)
    const accountEquity = availableBalance;
    // New margin health
    const health = newMaintenanceMarginRequired > 0 ? accountEquity / newMaintenanceMarginRequired * 100 : 10000; // Very high if position is fully closed
    return health;
  }, [useMargin, total, availableBalance, sliderValue, reduceOnly, currentMarginHealth]);
  const getLeverageColor = (leverage: number) => {
    if (leverage === 0) return 'text-text-tertiary';
    if (leverage < 2) return 'text-success';
    if (leverage < 5) return 'text-warning';
    return 'text-danger';
  };
  const getLeverageDotColor = (leverage: number) => {
    if (leverage === 0) return 'bg-text-tertiary';
    if (leverage < 2) return 'bg-success';
    if (leverage < 5) return 'bg-warning';
    return 'bg-danger';
  };
  return <div className="flex flex-col h-full bg-background-primary" data-id="element-1586">
      {/* Top Header with Tabs */}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-border-subtle" data-id="element-1587">
        <button className="px-3 py-1.5 text-sm text-text-secondary hover:text-text-primary rounded transition-colors" data-id="element-1588">
          Alerts
        </button>
        <button className="flex items-center gap-2 px-3 py-1.5 text-sm bg-background-elevated text-text-primary rounded" data-id="element-1589">
          Order form
          <X size={14} className="text-text-secondary" data-id="element-1590" />
        </button>
        <button className="p-1.5 hover:bg-background-elevated rounded transition-colors" data-id="element-1591">
          <Plus size={16} className="text-text-secondary" data-id="element-1592" />
        </button>

        <div className="flex-1" data-id="element-1593" />

        <button className="p-1.5 hover:bg-background-elevated rounded transition-colors" data-id="element-1594">
          <Maximize2 size={16} className="text-text-secondary" data-id="element-1595" />
        </button>
        <button className="p-1.5 hover:bg-background-elevated rounded transition-colors" data-id="element-1596">
          <Settings size={16} className="text-text-secondary" data-id="element-1597" />
        </button>
      </div>

      {/* Buy/Sell Toggle & Order Type */}
      <div className="flex items-center justify-between px-4 py-4" data-id="element-1598">
        <div className="flex items-center gap-2 bg-background-secondary rounded-xl p-1" data-id="element-1599">
          <button onClick={() => setOrderSide('buy')} className={`
              px-6 py-1.5 text-sm font-medium rounded-xl transition-all
              ${orderSide === 'buy' ? 'bg-[#1E3A32] text-success' : 'text-text-secondary hover:text-text-primary'}
            `} data-id="element-1600">
            Buy
          </button>
          <button onClick={() => setOrderSide('sell')} className={`
              px-6 py-1.5 text-sm font-medium rounded-xl transition-all
              ${orderSide === 'sell' ? 'bg-[#3D2832] text-[#FF9EB5]' : 'text-text-secondary hover:text-text-primary'}
            `} data-id="element-1601">
            Sell
          </button>
        </div>

        <Dropdown value={orderType} options={orderTypeOptions} onChange={handleOrderTypeChange} showLearnMore onLearnMore={() => window.open('https://support.kraken.com/hc/en-us/articles/203325783-Order-types', '_blank')} data-id="element-1602" />
      </div>

      {/* Available to Trade Card */}
      <div className="mx-4 mb-4 p-4 bg-background-secondary rounded-lg border border-border-subtle" data-id="element-1603">
        <div className="flex items-start justify-between mb-3" data-id="element-1604">
          <div data-id="element-1605">
            <div className="text-xs text-text-secondary mb-1" data-id="element-1606">
              {useMargin ? 'Available to trade' : 'Available balance'}
            </div>
            <div className="flex items-center gap-2" data-id="element-1607">
              <span className="text-lg font-semibold font-mono text-text-primary" data-id="element-1608">
                {effectiveBalance.toFixed(2)}
              </span>
              <span className="text-sm text-text-secondary" data-id="element-1609">{currency}</span>
              <button className="p-1 hover:bg-background-elevated rounded transition-colors" data-id="element-1610">
                <Plus size={14} className="text-text-secondary" data-id="element-1611" />
              </button>
            </div>
          </div>

          <div className="flex items-center gap-2" data-id="element-1612">
            <button className="p-1 hover:bg-background-elevated rounded transition-colors" data-id="element-1613">
              <Info size={16} className="text-text-secondary" data-id="element-1614" />
            </button>
            <span className={`text-sm font-medium ${useMargin ? 'text-success' : 'text-text-secondary'}`} data-id="element-1615">
              Margin (10x)
            </span>
            <button onClick={() => setUseMargin(!useMargin)} className={`
                relative w-11 h-6 rounded-full transition-all duration-200
                ${useMargin ? 'bg-success hover:bg-success-light' : 'bg-background-elevated hover:bg-border-default'}
                focus:outline-none focus:ring-2 focus:ring-brand-purple/50 focus:ring-offset-2 focus:ring-offset-background-primary
              `} data-id="element-1616">
              <span className={`
                  absolute top-0.5 left-0.5 w-5 h-5 rounded-full 
                  transition-all duration-200 ease-out
                  ${useMargin ? 'translate-x-5 bg-white' : 'translate-x-0 bg-text-tertiary'}
                  shadow-sm
                `} data-id="element-1617" />
            </button>
          </div>
        </div>

        {/* Progress Bar - Margin Health Indicator */}
        <div className="w-full h-1 bg-background-elevated rounded-full overflow-hidden" data-id="element-1618">
          <div className={`h-full rounded-full transition-all duration-200 ${getMarginHealthColor()}`} style={{
          width: `${marginHealth}%`
        }} data-id="element-1619" />
        </div>
      </div>

      {/* Form Content - Scrollable middle section */}
      <div className="flex-1 px-4 space-y-4 overflow-y-auto min-h-0" data-id="element-1620">
        {/* Market Price or Limit Price */}
        {orderType === 'Limit' ? <div data-id="element-1621">
            <div className="text-xs text-text-secondary mb-2" data-id="element-1622">Limit price</div>
            <div className="relative" data-id="element-1623">
              <input type="text" value={limitPrice} onChange={e => setLimitPrice(e.target.value)} className="w-full pl-3 pr-14 py-2.5 bg-background-elevated border-0 rounded-md text-sm font-mono text-text-primary placeholder:text-text-tertiary outline-none focus:shadow-[0_0_0_2px_rgb(122,46,255),0_0_0_8px_rgba(122,46,255,0.3)] transition-all" placeholder={currentPrice.toFixed(1)} data-id="element-1624" />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-text-secondary font-medium" data-id="element-1625">
                {currency}
              </span>
            </div>
          </div> : <div data-id="element-1626">
            <div className="text-xs text-text-secondary mb-2" data-id="element-1627">Market price</div>
            <div className="flex items-center justify-between px-4 py-2.5 bg-background-elevated rounded-md" data-id="element-1628">
              <span className="font-mono text-sm text-text-primary" data-id="element-1629">
                ≈ {currentPrice.toLocaleString()}
              </span>
              <span className="text-sm text-text-secondary font-medium" data-id="element-1630">
                {currency}
              </span>
            </div>
          </div>}

        {/* Quantity & Total Grid */}
        <div className="grid grid-cols-2 gap-3" data-id="element-1631">
          <div data-id="element-1632">
            <div className="text-xs text-text-secondary mb-2" data-id="element-1633">Quantity</div>
            <div className="relative" data-id="element-1634">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-text-secondary" data-id="element-1635">
                ≈
              </span>
              <input type="text" value={quantity} onChange={e => handleQuantityChange(e.target.value)} className="w-full pl-8 pr-14 py-2.5 bg-background-elevated border-0 rounded-md text-sm font-mono text-text-primary placeholder:text-text-tertiary outline-none focus:shadow-[0_0_0_2px_rgb(122,46,255),0_0_0_8px_rgba(122,46,255,0.3)] transition-all" placeholder="0.00000000" data-id="element-1636" />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-text-secondary font-medium" data-id="element-1637">
                BTC
              </span>
            </div>
          </div>

          <div data-id="element-1638">
            <div className="text-xs text-text-secondary mb-2" data-id="element-1639">Total</div>
            <div className="relative" data-id="element-1640">
              <input type="text" name="total" value={totalInput} onChange={e => handleTotalChange(e.target.value)} className="w-full pl-3 pr-14 py-2.5 bg-background-elevated border-0 rounded-md text-sm font-mono text-text-primary placeholder:text-text-tertiary outline-none focus:shadow-[0_0_0_2px_rgb(122,46,255),0_0_0_8px_rgba(122,46,255,0.3)] transition-all" placeholder="0.00" data-id="element-1641" />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-text-secondary font-medium" data-id="element-1642">
                {currency}
              </span>
            </div>
          </div>
        </div>

        {/* Error Messages */}
        {total < 5 && total > 0 && <div className="px-4 py-2.5 bg-danger/10 border border-danger/20 rounded-md" data-id="element-1643">
            <p className="text-sm text-danger" data-id="element-1644">Cannot be smaller than 5 USD</p>
          </div>}

        {total > effectiveBalance && <div className="px-4 py-2.5 bg-danger/10 border border-danger/20 rounded-md" data-id="element-1645">
            <p className="text-sm text-danger" data-id="element-1646">
              Insufficient balance. Available: {effectiveBalance.toFixed(2)}{' '}
              {currency}
            </p>
          </div>}

        {/* Slider */}
        <div className="relative" data-id="element-1647">
          {/* Slider markers */}
          <div className="absolute top-0 left-0 right-0 flex justify-between px-0.5 pointer-events-none" data-id="element-1648">
            <div className="w-px h-1 bg-border-subtle" style={{
            marginLeft: '25%'
          }} data-id="element-1649" />
            <div className="w-px h-1 bg-border-subtle" style={{
            marginLeft: '25%'
          }} data-id="element-1650" />
            <div className="w-px h-1 bg-border-subtle" style={{
            marginLeft: '25%'
          }} data-id="element-1651" />
          </div>

          <input type="range" min="0" max="100" value={sliderValue} onChange={e => setSliderValue(parseInt(e.target.value))} className="w-full h-1 bg-background-elevated rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:shadow-md relative z-10" data-id="element-1652" />

          <div className="flex justify-between text-xs text-text-tertiary mt-1.5" data-id="element-1653">
            <button onClick={() => setSliderValue(0)} className="hover:text-text-primary transition-colors cursor-pointer" data-id="element-1654">
              0%
            </button>
            <button onClick={() => setSliderValue(25)} className="hover:text-text-primary transition-colors cursor-pointer" data-id="element-1655">
              25%
            </button>
            <button onClick={() => setSliderValue(50)} className="hover:text-text-primary transition-colors cursor-pointer" data-id="element-1656">
              50%
            </button>
            <button onClick={() => setSliderValue(75)} className="hover:text-text-primary transition-colors cursor-pointer" data-id="element-1657">
              75%
            </button>
            <button onClick={() => setSliderValue(100)} className="hover:text-text-primary transition-colors cursor-pointer" data-id="element-1658">
              100%
            </button>
          </div>
        </div>

        {/* Leverage Info - Only show when margin is enabled */}
        {useMargin && <div className="flex items-start justify-between text-xs py-1" data-id="element-1659">
            <div className="flex flex-col gap-1" data-id="element-1660">
              <span className="text-text-secondary" data-id="element-1661">Portfolio leverage</span>
              <div className="flex items-center gap-1.5 font-mono" data-id="element-1662">
                <span className={`w-1.5 h-1.5 rounded-full ${getLeverageDotColor(currentLeverage)}`} data-id="element-1663" />
                <span className={getLeverageColor(currentLeverage)} data-id="element-1664">
                  {currentLeverage.toFixed(2)}x
                </span>
                <span className="text-text-secondary" data-id="element-1665">→</span>
                <span className={`w-1.5 h-1.5 rounded-full ${getLeverageDotColor(newLeverage)}`} data-id="element-1666" />
                <span className={getLeverageColor(newLeverage)} data-id="element-1667">
                  {newLeverage.toFixed(2)}x
                </span>
              </div>
            </div>

            <div className="flex flex-col gap-1 items-end" data-id="element-1668">
              <span className="text-text-secondary" data-id="element-1669">Margin health</span>
              <span className="font-mono text-text-primary" data-id="element-1670">
                {currentMarginHealth && newMarginHealth ? <>
                    {currentMarginHealth.toFixed(2)}% →{' '}
                    {newMarginHealth.toFixed(2)}%
                  </> : '-%'}
              </span>
            </div>
          </div>}

        {/* Reduce Only & TP/SL */}
        <div className="flex items-center justify-between py-1" data-id="element-1671">
          <label className="flex items-center gap-2 cursor-pointer group" data-id="element-1672">
            <div className="relative" data-id="element-1673">
              <input type="checkbox" checked={reduceOnly} onChange={e => setReduceOnly(e.target.checked)} className="w-5 h-5 rounded bg-background-elevated border border-border-default checked:bg-brand-purple checked:border-brand-purple appearance-none cursor-pointer transition-all" data-id="element-1674" />
              {reduceOnly && <svg className="absolute inset-0 w-5 h-5 text-white pointer-events-none" viewBox="0 0 20 20" fill="none" data-id="element-1675">
                  <path d="M6 10l2 2 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-1676" />
                </svg>}
            </div>
            <span className="text-sm text-text-secondary border-b border-dotted border-text-tertiary cursor-help" title="Only reduce existing positions" data-id="element-1677">
              Reduce only
            </span>
          </label>

          <div className="flex items-center gap-2" data-id="element-1678">
            <span className="text-sm text-text-secondary" data-id="element-1679">TP/SL</span>
            <Dropdown value={tpSlValue} options={tpSlOptions} onChange={setTpSlValue} className="min-w-[100px]" data-id="element-1680" />
          </div>
        </div>
      </div>

      {/* Fixed Bottom Section - Fee Breakdown */}
      <div className="px-4 pt-4 pb-3 space-y-2.5 border-t border-border-subtle bg-background-primary" data-id="element-1681">
        <div className="flex items-center justify-between text-xs" data-id="element-1682">
          <span className="text-text-secondary border-b border-dotted border-text-tertiary cursor-help" title="Margin required for this position" data-id="element-1683">
            Required margin
          </span>
          <span className="font-mono text-text-primary" data-id="element-1684">
            0.48 <span className="text-sm text-text-secondary" data-id="element-1685">{currency}</span>
          </span>
        </div>
        <div className="flex items-center justify-between text-xs" data-id="element-1686">
          <span className="text-text-secondary border-b border-dotted border-text-tertiary cursor-help" title="Estimated trading fee" data-id="element-1687">
            Est. trading fee
          </span>
          <div className="flex items-center gap-1" data-id="element-1688">
            <span className="font-mono text-text-primary" data-id="element-1689">
              {feeCurrency === 'USD' ? displayedFee.toFixed(4) : displayedFee.toFixed(8)}
            </span>
            <Dropdown value={feeCurrency} options={feeCurrencyOptions} onChange={setFeeCurrency} variant="inline" data-id="element-1690" />
          </div>
        </div>
        <div className="flex items-center justify-between text-xs" data-id="element-1691">
          <span className="text-text-secondary border-b border-dotted border-text-tertiary cursor-help" title="Estimated margin fee per 4 hours" data-id="element-1692">
            Est. margin fee
          </span>
          <span className="font-mono text-text-primary" data-id="element-1693">
            0.0024{' '}
            <span className="text-sm text-text-secondary" data-id="element-1694">{currency}</span> / 4
            hrs
          </span>
        </div>
      </div>

      {/* Fixed Bottom Section - Submit Button */}
      <div className="px-4 pb-4 bg-background-primary" data-id="element-1695">
        <button onClick={handleSubmit} className={`
            w-full py-3 rounded-lg font-semibold text-base transition-all
            ${orderSide === 'buy' ? 'bg-success hover:bg-success-light text-background-primary' : 'bg-[#E85D75] hover:bg-[#F06D84] text-background-primary'}
          `} data-id="element-1696">
          {orderSide === 'buy' ? 'Buy' : 'Sell'} BTC/{currency} (
          {leverageMultiplier}x)
        </button>
      </div>
    </div>;
}